import axios, { AxiosInstance } from 'axios';
import {
  TranslateResponse,
  ExecuteResponse,
  ReportWidget,
  ChartType
} from '../types/visualization';
import { AuthResponse, LoginCredentials, User } from '../types/auth';

// Configuration
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_BASE_URL = process.env.REACT_APP_API_URL ?? '/api';

class ApiService {
  private api: AxiosInstance;
  private authToken: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000, // 30 seconds timeout
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        if (this.authToken) {
          config.headers.Authorization = `Bearer ${this.authToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.logout();
          window.location.href = '/login';
        }
        console.error('API Error:', error);
        return Promise.reject(error);
      }
    );

    // Load token from localStorage on initialization
    this.loadAuthToken();
  }

  // Authentication methods
  private loadAuthToken() {
    const token = localStorage.getItem('auth_token');
    if (token) {
      this.authToken = token;
    }
  }

  private saveAuthToken(token: string) {
    this.authToken = token;
    localStorage.setItem('auth_token', token);
  }

  private clearAuthToken() {
    this.authToken = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/login', credentials);
    const { token, user } = response.data;
    
    this.saveAuthToken(token);
    localStorage.setItem('current_user', JSON.stringify(user));
    
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<{ user: User }>('/auth/me');
    return response.data.user;
  }

  logout() {
    this.clearAuthToken();
  }

  isAuthenticated(): boolean {
    return !!this.authToken;
  }

  getCurrentUserFromStorage(): User | null {
    const userStr = localStorage.getItem('current_user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // NL2SQL AI-Powered Reports methods
  async translateNaturalLanguageToSQL(query: string): Promise<TranslateResponse> {
    const response = await this.api.post('/nl2sql/translate', { query });
    return response.data;
  }

  async executeSQL(sqlQuery: string, options?: {
    chartType?: ChartType;
    config?: any;
    metadata?: any;
    title?: string;
  }): Promise<ExecuteResponse> {
    const response = await this.api.post('/nl2sql/execute', {
      sql_query: sqlQuery,
      ...options
    });
    return response.data;
  }

  async getSchema(): Promise<{
    schema: string;
    examples: Array<{ question: string; sql: string }>;
  }> {
    const response = await this.api.get('/nl2sql/schema');
    return response.data;
  }

  // Widget management methods
  async getReportWidgets(): Promise<{ widgets: ReportWidget[] }> {
    const response = await this.api.get('/nl2sql/widgets');
    return response.data;
  }

  async createReportWidget(widget: {
    name: string;
    nl_query: string;
    sql_query: string;
    visualization_type: ChartType;
    config?: any;
    metadata?: any;
  }): Promise<{ widget: ReportWidget }> {
    const response = await this.api.post('/nl2sql/widgets', widget);
    return response.data;
  }

  async deleteReportWidget(widgetId: number): Promise<{ success: boolean }> {
    const response = await this.api.delete(`/nl2sql/widgets/${widgetId}`);
    return response.data;
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
